import { ApiService } from "../../../../config";
import useUserStore from "../../../../store/user";

/**
 * Tenta uma lista de paths em ordem, primeiro via ApiService e depois GBP base (ou vice-versa).
 * Suporta fallback automático entre servidores.
 */
export async function tryApiPaths(
  method: "get" | "post" | "put" | "patch" | "delete",
  paths: string[],
  data?: unknown,
  options?: { preferGbpBase?: boolean; gbpOnly?: boolean },
) {
  const preferGbpBase = options?.preferGbpBase ?? false;
  const gbpOnly = options?.gbpOnly ?? false;
  
  // gbpOnly implica preferGbpBase
  const shouldPreferGbp = preferGbpBase || gbpOnly;
  const gbpBaseRaw =
    import.meta.env.VITE_APP_URL_GBP || import.meta.env.VITE_APP_GBP_BASE;
  const gbpBase = gbpBaseRaw ? gbpBaseRaw.replace(/\/$/, "") : null;
  const urlBase = import.meta.env.VITE_APP_URL_BASE || "";

  const attempts: Array<{
    url: string;
    via: "api" | "gbp";
    status?: number;
    error?: string;
  }> = [];

  for (const path of paths) {
    // normalize path for ApiService: remove leading '/api' if urlBase already contains '/api'
    const apiPath =
      path.startsWith("/api") && urlBase.includes("/api")
        ? path.replace(/^\/api/, "")
        : path;

    const tryGbp = async () => {
      if (!gbpBase) return null;

      const hasBaseApi = gbpBase.endsWith("/api");
      const hasPathApi = path.startsWith("/api");

      let finalUrl = "";
      if (hasBaseApi && hasPathApi) {
        finalUrl = `${gbpBase}${path.substring(4)}`;
      } else if (!hasBaseApi && !hasPathApi) {
        finalUrl = `${gbpBase}/api${path.startsWith("/") ? path : "/" + path}`;
      } else {
        finalUrl = `${gbpBase}${path.startsWith("/") ? path : "/" + path}`;
      }

      try {
        const headers: Record<string, string> = { Accept: "application/json" };
        if (method !== "get" && method !== "delete")
          headers["Content-Type"] = "application/json";
        // Endpoints GBP (/api/businesses, /api/locations) exigem JWT desde a
        // protecao por perfil; o fetch cru tambem precisa enviar o Bearer.
        const { user } = useUserStore.getState();
        if (user?.token) {
          headers["Authorization"] = `Bearer ${user.token}`;
        }

        const jsonBody =
          method !== "get" && method !== "delete"
            ? JSON.stringify(data)
            : undefined;

        if (jsonBody) {
          console.log(`[httpClient.tryGbp] ${method.toUpperCase()} ${finalUrl}`);
          console.log(`  data object:`, data);
          console.log(`  json body:`, jsonBody);
          // Verificar se há campos com nome vazio
          if (data && typeof data === "object") {
            const keys = Object.keys(data);
            const emptyKeyFields = keys.filter(k => k === "");
            if (emptyKeyFields.length > 0) {
              console.error(`  ⚠️ ALERT: Found ${emptyKeyFields.length} fields with empty key names!`, data);
            }
          }
        }

        const res = await fetch(finalUrl, {
          method: method.toUpperCase(),
          headers,
          body: jsonBody,
        });

        let bodyText: string | null = null;
        try {
          bodyText = await res.text();
        } catch {
          bodyText = null;
        }

        attempts.push({
          url: finalUrl,
          via: "gbp",
          status: res.status,
          error: !res.ok ? (bodyText ?? `HTTP ${res.status}`) : undefined,
        });

        if (res.ok) {
          let parsed: unknown;
          try {
            parsed = bodyText ? JSON.parse(bodyText) : null;
          } catch {
            parsed = bodyText;
          }
          return { data: parsed } as { data: unknown };
        }

        return null;
      } catch (e) {
        attempts.push({
          url: finalUrl,
          via: "gbp",
          error: e instanceof Error ? e.message : String(e),
        });
        return null;
      }
    };

    if (shouldPreferGbp && gbpBase) {
      const gbpRes = await tryGbp();
      if (gbpRes) return gbpRes;

      // gbpOnly: não faz fallback para a API Georanking (rota inexistente lá)
      if (gbpOnly) continue;

      try {
        // @ts-ignore - generic method accessor
        const res = await (ApiService as any)[method](apiPath, data);
        return res;
      } catch (e) {
        const errAny = e as any;
        const status = errAny?.response?.status;
        let body: any = null;
        try {
          body = errAny?.response?.data ?? null;
        } catch {
          body = null;
        }
        attempts.push({
          url: `${urlBase}${apiPath}`,
          via: "api",
          status,
          error: body
            ? JSON.stringify(body)
            : (errAny?.message ?? String(e)),
        });
      }
    } else {
      try {
        // @ts-ignore - generic method accessor
        const res = await (ApiService as any)[method](apiPath, data);
        return res;
      } catch (e) {
        const errAny = e as any;
        const status = errAny?.response?.status;
        let body: any = null;
        try {
          body = errAny?.response?.data ?? null;
        } catch {
          body = null;
        }
        attempts.push({
          url: `${urlBase}${apiPath}`,
          via: "api",
          status,
          error: body
            ? JSON.stringify(body)
            : (errAny?.message ?? String(e)),
        });
      }

      const gbpRes = await tryGbp();
      if (gbpRes) return gbpRes;
    }
  }

  const summary = attempts
    .map(
      (a) =>
        `${a.via.toUpperCase()} ${a.url} ${a.status ? `status:${a.status}` : ""} ${a.error ? `error:${a.error}` : ""}`,
    )
    .join(" | ");
  throw new Error(`tryApiPaths: all attempts failed. Attempts: ${summary}`);
}
